/*
 * Features: Team membership administration and user roster.
 * Implementation: Database membership guards all authority-changing operations.
 * Recent changes: Includes uncached access levels without changing team membership semantics.
 */
import { z } from "zod";
import { DataRoutes } from "./types";

const requireUserAdmin = `
  IF NOT EXISTS (
    SELECT 1 FROM dbo.UserTeams UT
    JOIN dbo.Teams T ON T.Id = UT.TeamId
    WHERE UT.UserId = dbo.fnGetContextUser() AND T.Name = 'User Admin'
  ) THROW 99310, 'Forbidden: User Admin membership required.', 1;
`;

const teamsRoutes: DataRoutes = {
  // Get list of all teams with their hierarchy
  'get /api/data/teams': {
    query: `SELECT
      Id as teamId,
      Name as teamName,
      parentId as parentId
    FROM Teams t`
  },

  // Delete a team by ID
  'delete /api/data/teams/:id': {
    schema: z.object({
      id: z.number().int().positive(),
    }),
    query: `${requireUserAdmin} DELETE FROM Teams WHERE Id = @id`
  },

  // Get all users with their team associations
  'get /api/data/teams/users': {
    query: `SELECT
      u.Id as userId,
      u.Name as name,
      u.Email as email,
      u.AccessLevel as accessLevel,
      t.Id as teamId,
      t.Name as teamName,
      t.parentId as parentId
    FROM Users u
    FULL OUTER JOIN UserTeams ut ON u.ID = ut.UserID
    FULL OUTER JOIN Teams t ON ut.TeamID = t.ID
    ORDER BY t.ID`,
    cache: false,
  },

  // Add a user to a team
  'post /api/data/teams/:id/users': {
    schema: z.object({
      id: z.number(),
      userId: z.number(),
    }),
    allowCrossUserAccess: true, // Admins can add other users to teams
    query: `${requireUserAdmin} INSERT INTO UserTeams (TeamID, UserID)
            VALUES (@id, @userId)`
  },

  // Remove a user from a team
  'delete /api/data/teams/:id/users/:userId': {
    schema: z.object({
      id: z.number(),
      userId: z.number(),
    }),
    allowCrossUserAccess: true, // Admins can remove other users from teams
    query: `${requireUserAdmin} DELETE FROM UserTeams
            WHERE TeamID = @id
            AND UserID = @userId`
  },
}

export default teamsRoutes;
